import { DatePicker } from "@racoon/ui";
import { Namespace, useTypedT } from "@racoon/i18n";
import { toTrendBadge } from "~/shared/utils/badgeFormatters";
import type { MonitorStats } from "../lib/use-monitor-stats.hook";
import type { WasteStatus } from "../../dashboard/lib/use-waste-status.hook";
import type { TruckProviderGroup } from "../../dashboard/lib/use-trucks-by-provider.hook";
import { MonitorStatCard } from "./monitor-stat-card";
import { TruckProviderPanel } from "./truck-provider-panel";

interface MonitorSidebarProps {
  selectedDate: Date;
  onSelectedDateChange: (date: Date) => void;
  monitorStats: MonitorStats;
  wasteStatus: WasteStatus;
  trucksOnLineTotal: number;
  trucksOnLinePercent: number;
  truckProviderGroups: TruckProviderGroup[];
}

export const MonitorSidebar = ({
  selectedDate,
  onSelectedDateChange,
  monitorStats,
  wasteStatus,
  trucksOnLineTotal,
  trucksOnLinePercent,
  truckProviderGroups,
}: MonitorSidebarProps) => {
  const t = useTypedT(Namespace.Govdash);

  return (
    <div className="flex h-full w-80 shrink-0 flex-col gap-2 overflow-hidden px-2 flex-1">
      <DatePicker
        value={selectedDate}
        onChange={(date) => date && onSelectedDateChange(date)}
        maxDate={new Date()}
        showDayNav
        navWrapperClassName="flex w-full items-center justify-between gap-1"
      />

      <div className="flex shrink-0 flex-col gap-2">
        <MonitorStatCard
          title={t("monitor.wasteWeight")}
          value={t("monitor.tonsValue", { value: monitorStats.wasteTons.toFixed(1) })}
          {...toTrendBadge(monitorStats.wasteTonsTrendPercent)}
        />
        <MonitorStatCard
          title={t("monitor.locationsVisited")}
          value={wasteStatus.locationVisited}
          badgeLabel={`${wasteStatus.locationVisitedPercent}%`}
          badgeColor="bg-green-500"
        />
        <MonitorStatCard
          title={t("monitor.trucksOnLine")}
          value={trucksOnLineTotal}
          badgeLabel={`${trucksOnLinePercent}%`}
          badgeColor="bg-purple-500"
        />
        <MonitorStatCard
          title={t("monitor.anomaliesDetected")}
          value={monitorStats.anomaliesDetected}
          {...toTrendBadge(monitorStats.anomaliesTrendPercent)}
        />
        <MonitorStatCard
          title={t("monitor.gasAnalyzerViolations")}
          value={t("monitor.gasAnalyzerRatio", { violations: 4, total: 20 })}
          badgeLabel="20%"
          badgeColor="bg-amber-500"
          blur={true}
        />
      </div>

      <TruckProviderPanel groups={truckProviderGroups} />
    </div>
  );
};
